// Client HTTP minimal — fetch wrapper avec gestion automatique du Bearer access token.
// Si une requête renvoie 401, on tente UN refresh, puis on rejoue la requête.
// Si le refresh échoue → on déconnecte (vidage du store).
//
// Pour le dev : le proxy Vite redirige /api/* vers http://localhost:3000.

import { useAuthStore } from '../store/authStore';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions {
  /** Si false, n'ajoute pas le Bearer token (utile pour /login, /register). */
  authenticated?: boolean;
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  body?: unknown,
  opts: ApiOptions = {},
): Promise<T> {
  const { authenticated = true } = opts;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (authenticated) {
    const accessToken = useAuthStore.getState().tokens?.accessToken;
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(path, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && authenticated) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      headers['Authorization'] = `Bearer ${refreshed.accessToken}`;
      const retry = await fetch(path, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      if (!retry.ok) throw await toApiError(retry);
      return (await retry.json()) as T;
    } else {
      useAuthStore.getState().clear();
      throw await toApiError(res);
    }
  }

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

async function toApiError(res: Response): Promise<ApiError> {
  try {
    const body = (await res.json()) as { error?: string; message?: string; details?: unknown };
    return new ApiError(res.status, body.error ?? 'UNKNOWN', body.message ?? res.statusText, body.details);
  } catch {
    return new ApiError(res.status, 'UNKNOWN', res.statusText);
  }
}

let refreshInFlight: Promise<{ accessToken: string; refreshToken: string } | null> | null = null;

async function tryRefresh(): Promise<{ accessToken: string; refreshToken: string } | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const refreshToken = useAuthStore.getState().tokens?.refreshToken;
    if (!refreshToken) return null;
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;
      const tokens = (await res.json()) as { accessToken: string; refreshToken: string };
      useAuthStore.getState().setTokens(tokens);
      return tokens;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

export const api = {
  get: <T>(path: string, opts?: ApiOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: ApiOptions) => request<T>('PATCH', path, body, opts),
  delete: <T>(path: string, opts?: ApiOptions) => request<T>('DELETE', path, undefined, opts),
};
